import { Injectable } from '@angular/core';
import { DaDataResponse, RawCompanyData, UserCompany } from '@company/const';
import { CompanyActions, CompanySelectors, StateWithCompany } from '@company/data-access';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilKeyChanged, filter, map } from 'rxjs/operators';
import { CompanyService } from './company.service';

@Injectable()
export class CompanyLazyService implements CompanyService {
  constructor(private store: Store<StateWithCompany>) {}

  searchCompany(query: string): void {
    this.store.dispatch(new CompanyActions.SearchCompany(query));
  }

  getSearchCompanyResult(): Observable<DaDataResponse[]> {
    return this.store.select(CompanySelectors.getSearchCompanyValue);
  }

  getSearchCompanyLoading(): Observable<boolean> {
    return this.store.select(CompanySelectors.getSearchCompanyLoading);
  }

  resetSearchCompanyResult(): void {
    this.store.dispatch(new CompanyActions.SearchCompanyReset());
  }

  addCompany(data: RawCompanyData): void {
    this.store.dispatch(new CompanyActions.AddCompany(data));
  }

  getAddCompanyLoading(): Observable<boolean> {
    return this.store.select(CompanySelectors.getAddCompanyLoading);
  }

  getAddedCompanyUid(): Observable<string | undefined> {
    return this.store.select(CompanySelectors.getAddCompanyValue);
  }

  resetAddedCompany(): void {
    this.store.dispatch(new CompanyActions.ResetAddedCompany());
  }

  selectCompany(companyUid: string): void {
    this.store.dispatch(new CompanyActions.SelectCompany(companyUid));
  }

  getSelectedCompany(): Observable<UserCompany | undefined> {
    return combineLatest([this.store.pipe(select(CompanySelectors.getSelectedCompany)), this.getUserCompanies()]).pipe(
      map(([uid, companies]) => companies.find((c) => c.uid === uid)),
    );
  }

  getSelectedCompanyUid(): Observable<string | undefined> {
    return this.getSelectedCompany().pipe(map((company) => company?.uid));
  }

  resetSelectedCompany(): void {
    this.store.dispatch(new CompanyActions.ResetSelectedCompany());
  }

  removeCompany(companyUid: string): void {
    this.store.dispatch(new CompanyActions.RemoveCompany(companyUid));
  }

  getRemoveCompanyLoading(): Observable<boolean> {
    return this.store.select(CompanySelectors.getRemoveCompanyLoading);
  }

  loadUserCompanies(): void {
    this.store.dispatch(new CompanyActions.LoadUserCompanies());
  }

  getUserCompaniesLoading(): Observable<boolean> {
    return this.store.select(CompanySelectors.getUserCompaniesLoading);
  }

  getUserCompanies(): Observable<UserCompany[]> {
    return this.store.select(CompanySelectors.getUserCompaniesState).pipe(
      filter((state) => Boolean(state && (state.success || state.error))),
      distinctUntilKeyChanged('value'),
      map((state) => state.value || []),
    );
  }
}
